import type { ExclusionCategory, ExclusionExportRow, BulkAmountRow } from '../../api/client';
import { triggerDownload, csvBlob, stamp } from '@cca/ui';

// Bulk exclusion-data template: export a church × year grid (one column per
// category, pre-filled with current amounts) as .xlsx or .csv, fill it offline
// from the financials, and upload it back. The category columns are keyed by
// `category_key` (stable) so a renamed label never breaks the round-trip; the
// readable Church / Reg / Gross columns are for the human and ignored on import.

const FIXED = ['church_id', 'Church', 'Reg', 'Year', 'Gross'] as const;

function headerRow(categories: ExclusionCategory[]): string[] {
  return [...FIXED, ...categories.map((c) => c.category_key)];
}

function dataRows(
  data: ExclusionExportRow[],
  categories: ExclusionCategory[],
): (string | number)[][] {
  return data.map((r) => [
    r.church_id,
    r.name,
    r.registration_number ?? '',
    r.financial_year,
    r.total_gross_income ?? '',
    ...categories.map((c) => r.category_amounts[c.category_key] ?? 0),
  ]);
}

export async function downloadTemplateXlsx(
  data: ExclusionExportRow[],
  categories: ExclusionCategory[],
): Promise<void> {
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  wb.creator = 'CCAANZ Diocesan Quota Tool';
  wb.created = new Date();
  const ws = wb.addWorksheet('Exclusions');
  ws.addRow(headerRow(categories));
  ws.getRow(1).font = { bold: true };
  for (const row of dataRows(data, categories)) ws.addRow(row);
  // A legend row mapping category_key → label, on a second sheet.
  const legend = wb.addWorksheet('Categories');
  legend.addRow(['category_key', 'label']);
  for (const c of categories) legend.addRow([c.category_key, c.label]);
  const buffer = await wb.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `CCAANZ_Quota_exclusions_${stamp()}.xlsx`,
  );
}

export function downloadTemplateCsv(
  data: ExclusionExportRow[],
  categories: ExclusionCategory[],
): void {
  // csvBlob = shared @cca/ui builder: csvCell-encoded cells + UTF-8 BOM for Excel.
  const blob = csvBlob([headerRow(categories), ...dataRows(data, categories)]);
  triggerDownload(blob, `CCAANZ_Quota_exclusions_${stamp()}.csv`);
}

// ── Parsing uploads ───────────────────────────────────────────────────────────

/** Parse one CSV line into fields, honouring double-quoted fields (church names
 *  contain commas). */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

/** Turn a parsed header + grid of cells into bulk amount rows: for each data
 *  row, one row per category column whose header matches a known category_key
 *  and whose cell is a non-negative number. */
function rowsFromGrid(
  header: string[],
  grid: (string | number)[][],
  categories: ExclusionCategory[],
): BulkAmountRow[] {
  const norm = header.map((h) => String(h).trim());
  const churchIdIdx = norm.indexOf('church_id');
  const yearIdx = norm.findIndex((h) => h.toLowerCase() === 'year');
  if (churchIdIdx < 0 || yearIdx < 0) {
    throw new Error('Template must keep the church_id and Year columns.');
  }
  const known = new Set(categories.map((c) => c.category_key));
  const catCols = norm.map((h, i) => ({ key: h, i })).filter((c) => known.has(c.key));
  if (catCols.length === 0) throw new Error('No recognised category columns found.');

  const out: BulkAmountRow[] = [];
  for (const cells of grid) {
    const church_id = String(cells[churchIdIdx] ?? '').trim();
    const financial_year = Number(cells[yearIdx]);
    if (!church_id || !Number.isFinite(financial_year)) continue;
    for (const { key, i } of catCols) {
      const raw = cells[i];
      if (raw === '' || raw === undefined || raw === null) continue;
      const amount = Number(String(raw).replace(/,/g, ''));
      if (!Number.isFinite(amount) || amount < 0) continue;
      out.push({ church_id, financial_year, category_key: key, amount });
    }
  }
  return out;
}

export async function parseUpload(
  file: File,
  categories: ExclusionCategory[],
): Promise<BulkAmountRow[]> {
  const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type.includes('csv');
  if (isCsv) {
    const text = await file.text();
    const lines = text
      .replace(/^\uFEFF/, '')
      .split(/\r?\n/)
      .filter((l) => l.trim().length > 0);
    const [headerLine, ...rest] = lines;
    if (!headerLine) return [];
    const header = parseCsvLine(headerLine);
    const grid = rest.map(parseCsvLine);
    return rowsFromGrid(header, grid, categories);
  }
  // .xlsx
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.worksheets[0];
  if (!ws) return [];
  const grid: (string | number)[][] = [];
  ws.eachRow((row) => {
    const values = row.values as (string | number | undefined)[];
    // exceljs row.values is 1-indexed (index 0 is undefined); normalise to 0-based.
    grid.push(
      values.slice(1).map((v) => (v === undefined || v === null ? '' : (v as string | number))),
    );
  });
  const [headerCells, ...bodyCells] = grid;
  if (!headerCells) return [];
  const header = headerCells.map((v) => String(v));
  return rowsFromGrid(header, bodyCells, categories);
}
